import { Card, DatePicker, Icon, message, Popover, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import debounce from 'lodash/debounce'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import HeaderScreen from 'src/components/shared/header-screen/HeaderScreen'
import FadeIn from 'src/components/styled/FadeIn'
import { getSpartacusLastSeenTooltip } from 'src/utils/spartacusDateRange'
import { hasActiveSpartacusFilters } from 'src/utils/spartacusFilters'
import CatchBoardDealModal from 'src/screens/catch-board/CatchBoardDealModal'
import {
  buildCatchBoardDealFormFromSpartacusVenue,
  warnCatchBoardHandoffDuplicateVenue,
} from 'src/utils/catchBoardHandoff'
import styled from 'styled-components/macro'
import SpartacusFilters from './SpartacusFilters'
import SpartacusStatsBar from './SpartacusStatsBar'
import SpartacusVenueTable from './SpartacusVenueTable'
import { useSpartacusData } from './useSpartacusData'
import { useSpartacusPageState } from './useSpartacusPageState'
import { getIsSpartacusMobileViewport } from './useSpartacusMobile'

const { RangePicker } = DatePicker
const dateFormat = 'YYYY-MM-DD'
const DATE_RANGE_DEBOUNCE_MS = 300

/**
 * Spartacus venue hunter.
 * @param {object} [props]
 * @param {boolean} [props.nested] — when true (Bridge mini tab), omit top-level
 *   page title / FadeIn but keep date range, stats, filters, table.
 */
const Spartacus = ({
  nested = false,
  onAddToCatchBoard,
  seedSearch = '',
  searchHandoffNonce = 0,
} = {}) => {
  const {
    state,
    ukDateRangeLabel,
    ukPriorDateRangeLabel,
    priorPeriodTooltip,
    statsQueryParams,
    venuesQueryParams,
    setDateRange,
    updateFilters,
    updatePagination,
    updateSort,
  } = useSpartacusPageState()

  const {
    venues,
    pagination,
    stats,
    venuesLoading,
    statsLoading,
    venuesError,
    statsError,
  } = useSpartacusData({ statsQueryParams, venuesQueryParams })

  const [localCatchBoardDraft, setLocalCatchBoardDraft] = useState(null)

  const handleAddToCatchBoard = useCallback(
    async venue => {
      if (onAddToCatchBoard) {
        await onAddToCatchBoard(venue)
        return
      }
      // Await so the toast lands before the local create modal opens.
      await warnCatchBoardHandoffDuplicateVenue({
        venueId: venue?.venueId,
        venueName: venue?.name || venue?.venueName,
      })
      setLocalCatchBoardDraft(buildCatchBoardDealFormFromSpartacusVenue(venue))
    },
    [onAddToCatchBoard],
  )
  const [filtersSubmittingAction, setFiltersSubmittingAction] = useState(null)
  const [filtersExpanded, setFiltersExpanded] = useState(
    () => !getIsSpartacusMobileViewport(),
  )

  useEffect(() => {
    if (!searchHandoffNonce) {
      return undefined
    }

    setFiltersExpanded(true)
    const next = String(seedSearch || '').trim()
    if (next) {
      updateFilters({ search: next })
    }

    const timeoutId = window.setTimeout(() => {
      const root = document.querySelector(
        '[data-testid="spartacus-filter-search"]',
      )
      const input =
        root && root.tagName === 'INPUT' ? root : root?.querySelector?.('input')
      if (input && typeof input.focus === 'function') {
        input.focus()
        if (typeof input.select === 'function') {
          input.select()
        }
      }
    }, 0)

    return () => window.clearTimeout(timeoutId)
  }, [searchHandoffNonce, seedSearch, updateFilters])

  const [pickerRange, setPickerRange] = useState(() => [
    moment(state.fromDate),
    moment(state.toDate),
  ])
  const committedDateRangeRef = useRef({
    fromDate: state.fromDate,
    toDate: state.toDate,
  })

  useEffect(() => {
    committedDateRangeRef.current = {
      fromDate: state.fromDate,
      toDate: state.toDate,
    }
    setPickerRange([moment(state.fromDate), moment(state.toDate)])
  }, [state.fromDate, state.toDate])

  const debouncedSetDateRange = useMemo(
    () =>
      debounce((fromDate, toDate) => {
        try {
          setDateRange(fromDate, toDate)
        } catch (error) {
          message.error(error.message || 'Date range is too large')
          const { fromDate: committedFrom, toDate: committedTo } =
            committedDateRangeRef.current
          setPickerRange([moment(committedFrom), moment(committedTo)])
        }
      }, DATE_RANGE_DEBOUNCE_MS),
    [setDateRange],
  )

  useEffect(() => () => debouncedSetDateRange.cancel(), [debouncedSetDateRange])

  useEffect(() => {
    if (filtersSubmittingAction && !statsLoading && !venuesLoading) {
      setFiltersSubmittingAction(null)
    }
  }, [filtersSubmittingAction, statsLoading, venuesLoading])

  const disableFutureDates = current =>
    current && current.valueOf() > moment().endOf('day').valueOf()

  const handleDateChange = (_dates, dateStrings) => {
    if (!dateStrings?.[0] || !dateStrings?.[1]) {
      return
    }

    const fromDate = new Date(dateStrings[0])
    const toDate = new Date(dateStrings[1])

    setPickerRange([moment(fromDate), moment(toDate)])
    debouncedSetDateRange(fromDate, toDate)
  }

  const handleApplyFilters = filters => {
    setFiltersSubmittingAction('apply')
    updateFilters(filters)
  }

  const handleClearFilters = () => {
    setFiltersSubmittingAction('clear')
    updateFilters({})
  }

  const filtersActive = hasActiveSpartacusFilters(state.filters)

  const dateRangeSubTitle = (
    <SubTitleBlock>
      <span>Showing {ukDateRangeLabel} (UK)</span>
      <Tooltip title={priorPeriodTooltip}>
        <PriorComparisonLine>
          Compared to {ukPriorDateRangeLabel}
          <InfoCircleOutlined />
        </PriorComparisonLine>
      </Tooltip>
    </SubTitleBlock>
  )

  const toolbar = (
    <Toolbar>
      <StyledRangePicker
        allowClear={false}
        disabledDate={disableFutureDates}
        format={dateFormat}
        value={pickerRange}
        onChange={handleDateChange}
      />
    </Toolbar>
  )

  const header = nested ? (
    <NestedHeader data-testid='spartacus-nested-header'>
      <NestedHeaderCopy>
        {dateRangeSubTitle}
        <Popover
          placement='left'
          title='Last seen tracking'
          content={getSpartacusLastSeenTooltip()}
        >
          <NestedInfoIcon type='question-circle' />
        </Popover>
      </NestedHeaderCopy>
      {toolbar}
    </NestedHeader>
  ) : (
    <HeaderScreen
      title='Spartacus'
      subTitle={dateRangeSubTitle}
      popoverTitle='Last seen tracking'
      popoverContent={getSpartacusLastSeenTooltip()}
    >
      {toolbar}
    </HeaderScreen>
  )

  const page = (
    <Page data-testid={nested ? 'spartacus-nested' : 'spartacus-page'}>
      {header}

      <SpartacusStatsBar
        stats={stats}
        loading={statsLoading}
        error={statsError}
        priorPeriodTooltip={priorPeriodTooltip}
      />

      <FiltersCard
        title={
          <FiltersToggle
            type='button'
            onClick={() => setFiltersExpanded(open => !open)}
            aria-expanded={filtersExpanded}
          >
            <FiltersTitleText>
              Filters
              {filtersActive ? (
                <ActiveFiltersTag>Active</ActiveFiltersTag>
              ) : null}
            </FiltersTitleText>
            <Icon type={filtersExpanded ? 'up' : 'down'} />
          </FiltersToggle>
        }
      >
        {filtersExpanded ? (
          <SpartacusFilters
            filters={state.filters}
            applyingFilters={filtersSubmittingAction === 'apply'}
            clearingFilters={filtersSubmittingAction === 'clear'}
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
          />
        ) : null}
      </FiltersCard>

      <TableCard title='Venues'>
        <SpartacusVenueTable
          venues={venues}
          pagination={pagination}
          sortBy={state.sortBy}
          sortDir={state.sortDir}
          loading={venuesLoading}
          error={venuesError}
          onPaginationChange={updatePagination}
          onSortChange={updateSort}
          onAddToCatchBoard={handleAddToCatchBoard}
        />
      </TableCard>

      {!onAddToCatchBoard ? (
        <CatchBoardDealModal
          visible={!!localCatchBoardDraft}
          createDraft={localCatchBoardDraft}
          primaryVenueNameHint={
            localCatchBoardDraft?.linkedVenues?.[0]?.name || ''
          }
          onClose={() => setLocalCatchBoardDraft(null)}
          onSaved={() => setLocalCatchBoardDraft(null)}
        />
      ) : null}
    </Page>
  )

  if (nested) {
    return page
  }

  return <FadeIn>{page}</FadeIn>
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 767px) {
    gap: ${({ theme }) => theme.spacing.md};
  }
`

const NestedHeader = styled.div`
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  width: 100%;
`

const NestedHeaderCopy = styled.div`
  align-items: flex-start;
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`

const NestedInfoIcon = styled(Icon)`
  color: ${({ theme }) => theme.colours.oliveGrey};
  cursor: help;
  margin-top: 2px;
`

const SubTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const PriorComparisonLine = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colours.oliveGrey};
  cursor: help;
  display: inline-flex;
  font-size: 13px;
  gap: 4px;
  width: fit-content;

  .anticon {
    color: ${({ theme }) => theme.colours.oliveGrey};
    font-size: 12px;
  }
`

const Toolbar = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: flex-end;

  @media (max-width: 767px) {
    justify-content: stretch;
    width: 100%;
  }
`

const StyledRangePicker = styled(RangePicker)`
  min-width: 280px;

  @media (max-width: 767px) {
    min-width: 0;
    width: 100%;
  }
`

const FiltersCard = styled(Card)`
  .ant-card-head {
    min-height: 48px;
    padding: 0 16px;
  }

  .ant-card-head-title {
    font-weight: 600;
    padding: 12px 0;
  }

  .ant-card-body {
    padding: 12px 24px 24px;
  }
`

const FiltersToggle = styled.button`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  padding: 0;
  width: 100%;
`

const FiltersTitleText = styled.span`
  align-items: center;
  display: inline-flex;
  font-size: 16px;
  font-weight: 600;
  gap: 8px;
`

const ActiveFiltersTag = styled.span`
  background: ${({ theme }) => theme.colours.lettuceGreen};
  border-radius: 999px;
  color: ${({ theme }) => theme.colours.seaweedGreen};
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  padding: 4px 8px;
`

const TableCard = styled(Card)`
  .ant-card-head-title {
    font-weight: 600;
  }

  @media (max-width: 767px) {
    .ant-card-body {
      padding: 12px;
    }
  }
`

export default Spartacus
